import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './DatePicker.module.css';
import datePickerIcon from '../icons/date-picker.svg';
import Separator from '../Separator';
import Select from '../select';
import BackButton from '../back-button/BackButton';
import useOnResize from '../hooks/useOnResize';
import { createPortal } from 'react-dom';
import useDropdownPosition from '../hooks/useDropdownPosition';
import useOnScrollOutside from '../hooks/useOnScrollOutside';
import { MONTH_LONG, daysInMonth } from '../utils/dateUtils';

/* ------------------------------------------------------------------ *
 * LOCAL-WALL-CLOCK HELPERS
 *
 * This component intentionally never performs real timezone
 * conversion. It reads and writes a plain "2026-08-14T16:39:00"
 * style string - the exact numbers shown on screen, nothing more.
 * The IANA zone that gives those numbers meaning is supplied
 * separately (the "Time Zone" dropdown next to this field) and is
 * combined with this value only when the request is built.
 *
 * We deliberately avoid `new Date(isoString)` and `.toISOString()`
 * for parsing/serializing, because both are inconsistent about
 * local-vs-UTC interpretation depending on the exact string shape
 * (date-only strings parse as UTC, date-time strings parse as local
 * system time). Parsing/formatting the numbers by hand removes that
 * ambiguity entirely - a JS Date is used here only as an in-memory
 * container for year/month/day/hour/minute, never as a real instant.
 * ------------------------------------------------------------------ */

const LOCAL_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

// Parse "2026-08-14T16:39:00" (or ":ss" omitted) into a wall-clock
// Date container - the numbers only, no zone interpretation.
const parseLocalDateTime = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    const match = LOCAL_DATETIME_RE.exec(value);
    if (!match) return null;
    const [, year, month, day, hour, minute, second] = match;
    return new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        second ? Number(second) : 0,
    );
  }

  return null;
};

const formatDisplay = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  const hours12 = date.getHours() % 12 || 12;
  const meridian = date.getHours() >= 12 ? 'PM' : 'AM';
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(hours12)}:${pad(date.getMinutes())} ${meridian}`;
};

// --- MOVED OUTSIDE to prevent re-mounting issues ---
const DatePickerContent = ({ date, onDateChange }) => {
  const monthStartsAt = new Date(
      date.getFullYear(),
      date.getMonth(),
      1,
  ).getDay();
  const daysInCurrentMonth = daysInMonth(date);
  const totalSlots = monthStartsAt + daysInCurrentMonth;
  const monthEndsAt = totalSlots % 7 === 0 ? 0 : 7 - (totalSlots % 7);

  const selectedDate = date.getDate();

  const setDay = (day) => {
    const newDate = new Date(date);
    newDate.setDate(day);
    onDateChange(newDate);
  };

  return (
      <div className={styles.DatePickerContent}>
        <div className={styles.contentChildren}>
          <MonthAndYear date={date} setDate={onDateChange} />
          <Separator />
          <div className={styles.dates}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <span key={day}>{day}</span>
            ))}
            {Array.from({ length: monthStartsAt }).map((_, ind) => (
                <span key={`start-${ind}`} className={styles.unSelectableDate}>
              {ind + 1}
            </span>
            ))}
            {Array.from({ length: daysInCurrentMonth }).map((_, index) => (
                <span
                    key={index}
                    className={[
                      styles.date,
                      selectedDate === index + 1 && styles.active,
                    ].join(' ')}
                    onClick={() => setDay(index + 1)}
                >
              {index + 1}
            </span>
            ))}
            {Array.from({ length: monthEndsAt }).map((_, ind) => (
                <span key={`end-${ind}`} className={styles.unSelectableDate}>
              {ind + 1}
            </span>
            ))}
          </div>
          <Separator />
          <Time date={date} setDate={onDateChange} />
        </div>
      </div>
  );
};

const DatePicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const datePickerRef = useRef(null);
  const portalRef = useRef(null);

  // Local wall-clock container - defaults to "now" read via local
  // getters, which is fine as a starting point since it's just a
  // seed for the picker UI, not something sent anywhere yet.
  const [date, setDate] = useState(() => parseLocalDateTime(value) || new Date());

  useEffect(() => {
    if (open) {
      setShouldRender(true);
    } else {
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const position = useDropdownPosition(datePickerRef, shouldRender, 1000);

  // 1. Sync Prop -> Local State
  useEffect(() => {
    if (value) {
      const incomingDate = parseLocalDateTime(value);
      if (!incomingDate) return;
      if (incomingDate.getTime() !== date.getTime()) {
        setDate(incomingDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // 2. Emit the plain local wall-clock string - not a Date object -
  // so nothing upstream can accidentally run it through
  // .toISOString() and silently convert it to UTC.
  const handleDateChange = useCallback(
      (dateOrUpdater) => {
        let newDate;

        if (typeof dateOrUpdater === 'function') {
          newDate = dateOrUpdater(date);
        } else {
          newDate = dateOrUpdater;
        }

        if (!newDate || isNaN(newDate.getTime())) return;
        if (newDate.getTime() === date.getTime()) return;

        // A. Trigger Parent Update (Side Effect)
        if (onChange) {
          onChange(newDate);
        }

        setDate(newDate);
      },
      [date, onChange],
  );

  const toggle = () => setOpen((prev) => !prev);
  const handleClose = useCallback((e) => {
    setOpen(false);
  }, []);

  useOnResize(handleClose);
  useOnScrollOutside(portalRef, handleClose);

  const handleClickOutside = (event) => {
    if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target) &&
        portalRef.current &&
        !portalRef.current.contains(event.target)
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
      <>
        <div className={styles.DatePicker} ref={datePickerRef}>
          <DatePickerTrigger {...{ date, open, toggle }} />
        </div>
        {open &&
            position &&
            createPortal(
                <div
                    ref={portalRef}
                    className={styles.contentWrapper}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                      position: 'fixed',
                      top: position.top !== null ? `${position.top}px` : 'auto',
                      bottom:
                          position.bottom !== null ? `${position.bottom}px` : 'auto',
                      left: `${position.left}px`,
                      zIndex: 9999,
                      width: 'max-content',
                      maxWidth: '100vw',
                    }}
                >
                  <DatePickerContent date={date} onDateChange={handleDateChange} />
                </div>,
                document.body,
            )}
      </>
  );
};

const DatePickerTrigger = ({ date, open, toggle }) => {
  return (
      <div
          onClick={toggle}
          className={[styles.datePicker, open && styles.datePickerOpen].join(' ')}
      >
        <span>{formatDisplay(date)}</span>
        <img src={datePickerIcon} alt='date picker' width={20} height={20} />
      </div>
  );
};

const MonthAndYear = ({ date, setDate }) => {
  const updateDate = (modifier) => {
    const newDate = new Date(date);
    newDate.setDate(1);
    modifier(newDate);

    const maxDays = daysInMonth(newDate);
    const originalDay = date.getDate();
    newDate.setDate(Math.min(originalDay, maxDays));

    setDate(newDate);
  };

  const nextMonth = () => updateDate((d) => d.setMonth(d.getMonth() + 1));
  const prevMonth = () => updateDate((d) => d.setMonth(d.getMonth() - 1));

  return (
      <div className={styles.monthAndYear}>
        <BackButton type='button' onClick={prevMonth} />
        <div style={{ display: 'flex', gap: '0.6rem' }}>
          <Select
              options={MONTH_LONG}
              active={date.getMonth()}
              triggerProps={{ className: styles.select }}
              childrenProps={{
                style: {
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  placeItems: 'center',
                },
              }}
              onOptionSelect={(_month, index) =>
                  updateDate((d) => d.setMonth(index))
              }
          />
          <Select
              options={Array.from({ length: 201 }, (_, i) => i + 1980)}
              active={date.getFullYear() - 1980}
              childrenProps={{
                style: {
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  placeItems: 'center',
                  maxHeight: '14rem',
                  overflowY: 'auto',
                },
              }}
              triggerProps={{ className: styles.select }}
              onOptionSelect={(_year, index) =>
                  updateDate((d) => d.setFullYear(index + 1980))
              }
          />
        </div>
        <BackButton
            style={{ transform: 'rotate(180deg)' }}
            onClick={nextMonth}
            type='button'
        />
      </div>
  );
};

const Time = ({ date, setDate }) => {
  const HOURS = Array.from({ length: 12 }, (_, i) =>
      (i + 1).toString().padStart(2, '0'),
  );
  const MINUTES = Array.from({ length: 60 }, (_, i) =>
      i.toString().padStart(2, '0'),
  );
  const MERIDIAN = ['AM', 'PM'];

  const updateTime = (modifier) => {
    const newDate = new Date(date);
    modifier(newDate);
    setDate(newDate);
  };

  const handleHourSelect = (_option, index) => {
    updateTime((d) => {
      const selectedHour12 = index + 1;
      const currentHours24 = d.getHours();
      const isPM = currentHours24 >= 12;

      let newHours24;
      if (isPM) newHours24 = selectedHour12 === 12 ? 12 : selectedHour12 + 12;
      else newHours24 = selectedHour12 === 12 ? 0 : selectedHour12;

      d.setHours(newHours24);
    });
  };

  const handleMinuteSelect = (_option, index) => {
    updateTime((d) => d.setMinutes(index));
  };

  const handleMeridianSelect = (_option, index) => {
    const isPMSelected = index === 1;
    updateTime((d) => {
      const h = d.getHours();
      if (isPMSelected && h < 12) d.setHours(h + 12);
      else if (!isPMSelected && h >= 12) d.setHours(h - 12);
    });
  };

  return (
      <div className={styles.timeWithMeridian}>
        <Select
            options={HOURS}
            active={(date.getHours() % 12 || 12) - 1}
            triggerProps={{ className: styles.select }}
            childrenProps={{
              style: {
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                placeItems: 'center',
              },
            }}
            onOptionSelect={handleHourSelect}
        />
        <span style={{ width: '3px' }}>{':'}</span>
        <Select
            options={MINUTES}
            active={date.getMinutes()}
            triggerProps={{ className: styles.select }}
            childrenProps={{
              style: {
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                placeItems: 'center',
                maxHeight: '14rem',
                overflowY: 'auto',
              },
            }}
            onOptionSelect={handleMinuteSelect}
        />
        <span style={{ width: '3px' }}>&nbsp;</span>
        <Select
            options={MERIDIAN}
            active={date.getHours() >= 12 ? 1 : 0}
            onOptionSelect={handleMeridianSelect}
            triggerProps={{ className: styles.select }}
        />
      </div>
  );
};

export default DatePicker;